import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { getRouteApi, useRouter } from '@tanstack/react-router';
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type Row,
  type RowSelectionState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { toast } from 'sonner';

import { type ClustersQueryResult, useClusters } from '@/api/generated';
import { DataTable } from '@/components/blocks/data-table/data-table';
import Header from '@/components/header';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Spinner } from '@/components/ui/spinner';
import { columns } from '@/routes/(app)/(clusters)/components/cluster-columns';
import ClusterFilters from '@/routes/(app)/(clusters)/components/cluster-filters';
import {
  type ClusterFiltersSchema,
  clusterFiltersSchema,
} from '@/routes/(app)/(clusters)/lib/schema';

const route = getRouteApi('/app/clusters');

type Cluster = ClustersQueryResult['data'][number];

// Check if all dataset tasks are the same for the given clusters
const isSameTask = (clustersToCompare: Array<Cluster>): boolean =>
  clustersToCompare.every(
    (cluster) => cluster.dataset.task === clustersToCompare[0]?.dataset.task
  );

const Clusters = () => {
  const { datasetId, task, models, date, createdBy, isEval } =
    route.useSearch();
  const router = useRouter();
  const { t } = useTranslation();
  const form = useForm<ClusterFiltersSchema>({
    defaultValues: {
      datasetId,
      task,
      models,
      date,
      createdBy,
      isEval: !!isEval,
    },
    resolver: zodResolver(clusterFiltersSchema),
  });
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'createdAt',
      desc: true,
    },
  ]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { data, isLoading } = useClusters({
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    order: sorting[0]?.desc ? 'desc' : 'asc',
    orderBy: sorting[0]?.id,
    datasetId,
    task,
    models,
    from: date?.from ? new Date(date.from).toISOString() : undefined,
    to: date?.to ? new Date(date.to).toISOString() : undefined,
    createdBy,
    isEval: !!isEval,
  });

  const clusterIds = useMemo(() => Object.keys(rowSelection), [rowSelection]);

  const table = useReactTable<Cluster>({
    state: {
      pagination,
      sorting,
      rowSelection,
    },
    getRowId: (row) => row.id,
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    manualPagination: true,
    pageCount: Math.ceil((data?.total ?? 0) / pagination.pageSize),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    manualSorting: true,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
  });

  const handleNavigate = useCallback(
    async (row: Row<Cluster>) => {
      await router.navigate({
        to: '/app/clusters/$clusterId',
        params: {
          clusterId: row.original.id,
        },
      });
    },
    [router]
  );

  const handleCompare = useCallback(async () => {
    if (!data?.data) {
      toast.error(t('clusters.errors.noExperiments'));
      return;
    }

    const clustersToCompare = data.data.filter((cluster) =>
      clusterIds.includes(cluster.id)
    );

    if (!isSameTask(clustersToCompare)) {
      toast.error(t('clusters.errors.differentTasks'));
      return;
    }

    await router.navigate({
      to: '/app/clusters/compare',
      search: {
        clusterIds,
      },
    });
  }, [clusterIds, data, router, t]);

  return (
    <Form {...form}>
      <Header
        title={t('clusters.title')}
        description={t('clusters.description')}
      />
      <div className='flex flex-row justify-between items-end'>
        <ClusterFilters />
        <Button
          disabled={clusterIds.length === 0}
          onClick={handleCompare}>
          {t('clusters.compare')}
        </Button>
      </div>

      {isLoading ? (
        <Spinner />
      ) : (
        <DataTable
          table={table}
          onRowClick={handleNavigate}
        />
      )}
    </Form>
  );
};

export default Clusters;
